import { useState } from "react";
import { Link } from "react-router-dom";
import { SUPPORT_EMAIL } from "../lib/runtimeConfig.js";
import { sendContactMessage } from "../lib/travelApi.js";
import Seo from "../components/Seo.jsx";

/*
 * Contact page. Light, premium, and distinct from the full Plan a Trip intake.
 * Phase 2B: the form validates locally, then sendContactMessage() (in
 * src/lib/travelApi.js) POSTs to https://api.513travel.com/travel/contact and
 * shows the success state on { ok: true }. On failure we show a friendly error
 * with an email fallback. (No email notifications yet — backend persists only.)
 */

const HELP_CHIPS = [
  "New trip", "Flights", "Stays", "Visas", "Experiences", "Africa trips",
  "Existing booking", "General enquiry", "Partnership", "Studio enquiry",
];

// Same country-code list/order as the Plan a Trip form. "Other" lets the
// visitor type a full international number themselves (no prefix added).
const COUNTRY_CODES = [
  { name: "Nigeria", dial: "+234" },
  { name: "Ghana", dial: "+233" },
  { name: "Kenya", dial: "+254" },
  { name: "Rwanda", dial: "+250" },
  { name: "South Africa", dial: "+27" },
  { name: "Tanzania", dial: "+255" },
  { name: "Uganda", dial: "+256" },
  { name: "Ethiopia", dial: "+251" },
  { name: "Senegal", dial: "+221" },
  { name: "Côte d'Ivoire", dial: "+225" },
  { name: "Cameroon", dial: "+237" },
  { name: "Morocco", dial: "+212" },
  { name: "Egypt", dial: "+20" },
  { name: "United Arab Emirates", dial: "+971" },
  { name: "United Kingdom", dial: "+44" },
  { name: "United States", dial: "+1" },
  { name: "France", dial: "+33" },
  { name: "Other", dial: "other" },
];

// Combine the selected dial code with the typed number into one string.
// "Other" carries no prefix — the visitor types the full number themselves.
function buildFullWhatsApp(countryCode, number) {
  const trimmed = String(number || "").trim();
  if (!trimmed) return "";
  if (!countryCode || countryCode === "other") return trimmed;
  return `${countryCode} ${trimmed}`;
}

const FAQS = [
  { q: "How fast do you reply?", a: "Usually within a short time, by email or WhatsApp during working hours." },
  { q: "Can I ask about a trip without paying?", a: "Yes. Requests and quotes are free and carry no obligation." },
  { q: "I already submitted a request — what next?", a: "Quote your reference here or on the Track Trip page and we'll pick up where you left off." },
  { q: "Do you only handle Africa?", a: "Africa is our heart, but we plan trips worldwide — Africa-first, not Africa-only." },
];

const INITIAL = {
  fullName: "", email: "", countryCode: "+234", whatsappNumber: "", subject: "",
  message: "", preferred: "Email", consent: false,
};

export default function Contact() {
  const [form, setForm] = useState(INITIAL);
  const [errors, setErrors] = useState({});
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const set = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    if (errors[k]) setErrors((e) => ({ ...e, [k]: undefined }));
  };

  async function handleSubmit(e) {
    e.preventDefault();
    const next = {};
    if (!form.fullName.trim()) next.fullName = "Please tell us your name.";
    if (!form.email.trim() && !form.whatsappNumber.trim()) next.contact = "Add an email or WhatsApp number so we can reply.";
    if (!form.message.trim()) next.message = "Let us know how we can help.";
    if (!form.consent) next.consent = "Please confirm to continue.";
    setErrors(next);
    if (Object.keys(next).length) {
      document.querySelector("[data-invalid='true']")?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    setSubmitting(true);
    setSubmitError("");
    try {
      const { ok } = await sendContactMessage({
        ...form,
        fullWhatsAppNumber: buildFullWhatsApp(form.countryCode, form.whatsappNumber),
      });
      if (!ok) throw new Error("not-ok");
      setSent(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setSubmitError(
        err && err.userMessage
          ? err.userMessage
          : "Something went wrong. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="page">
      <Seo
        title="Contact 513Travel"
        description="Contact 513Travel for new trips, flights, stays, visas, experiences, Africa travel, existing bookings, partnerships and general enquiries."
        path="/contact"
      />
      {/* ===== Hero ===== */}
      <section className="page-hero">
        <div className="container">
          <div className="eyebrow">Contact</div>
          <h1>Let's plan it properly</h1>
          <p className="lede">
            Whether you're ready to plan, need help with an existing request, or
            just want to ask a question, our team is here to help.
          </p>
          <div className="page-actions">
            <Link to="/plan-a-trip" className="btn gold">Plan a Trip</Link>
            <Link to="/track-trip" className="btn outline">Track Trip</Link>
          </div>
        </div>
      </section>

      {/* ===== Contact method cards ===== */}
      <section className="section section--warm">
        <div className="container">
          <div className="section-head">
            <div className="eyebrow">Reach us</div>
            <h2>However suits you</h2>
          </div>
          <div className="grid grid-4">
            <a className="service-card" href={`mailto:${SUPPORT_EMAIL}`}>
              <h3>Email us</h3>
              <p>Reach the team directly at {SUPPORT_EMAIL}.</p>
              <span className="service-link">{SUPPORT_EMAIL} →</span>
            </a>
            <a className="service-card" href="#contact-form">
              <h3>WhatsApp support</h3>
              <p>Quick questions and trip updates once your request is in.</p>
              <span className="service-link">Send a message →</span>
            </a>
            <Link className="service-card" to="/plan-a-trip">
              <h3>Plan a new trip</h3>
              <p>Share your destination, dates and style for a tailored quote.</p>
              <span className="service-link">Start planning →</span>
            </Link>
            <Link className="service-card" to="/track-trip">
              <h3>Track a request</h3>
              <p>Check the status of an existing request or booking.</p>
              <span className="service-link">Track existing →</span>
            </Link>
          </div>
        </div>
      </section>

      {/* ===== Help chips ===== */}
      <section className="section">
        <div className="container">
          <div className="section-head">
            <div className="eyebrow">Tell us the topic</div>
            <h2>What can we help with?</h2>
          </div>
          <div className="route-chips">
            {HELP_CHIPS.map((c) => <span className="route-chip" key={c}>{c}</span>)}
          </div>
        </div>
      </section>

      {/* ===== Form ===== */}
      <section className="section section--warm" id="contact-form">
        <div className="container">
          <div className="section-head">
            <div className="eyebrow">Send a note</div>
            <h2>{sent ? "Thank you" : "Drop us a message"}</h2>
          </div>

          {sent ? (
            <div className="info-card success-card">
              <span className="success-tick" aria-hidden="true">✓</span>
              <p className="success-label">Message ready</p>
              <p className="success-note">
                Thanks, {form.fullName.split(" ")[0] || "there"} — we'll be in touch by{" "}
                {form.preferred.toLowerCase()} shortly. You can also reach us anytime at{" "}
                <a className="inline-link" href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
              </p>
              <div className="page-actions" style={{ justifyContent: "center" }}>
                <button type="button" className="btn gold" onClick={() => { setForm(INITIAL); setSent(false); setSubmitError(""); }}>
                  Send another message
                </button>
                <Link to="/plan-a-trip" className="btn outline">Plan a Trip</Link>
              </div>
            </div>
          ) : (
            <form className="form-card" onSubmit={handleSubmit} noValidate style={{ maxWidth: "640px" }}>
              <div className="field" data-invalid={errors.fullName ? "true" : undefined}>
                <label htmlFor="c-name">Full name</label>
                <input id="c-name" type="text" value={form.fullName} onChange={(e) => set("fullName", e.target.value)} />
                {errors.fullName && <p className="field-error">{errors.fullName}</p>}
              </div>
              <div className="field" data-invalid={errors.contact ? "true" : undefined}>
                <label htmlFor="c-email">Email address</label>
                <input id="c-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} />
              </div>
              <div className="field" data-invalid={errors.contact ? "true" : undefined}>
                <label htmlFor="c-wa">WhatsApp number</label>
                <div className="phone-row">
                  <select
                    aria-label="Country dialing code"
                    className="phone-code"
                    value={form.countryCode}
                    onChange={(e) => set("countryCode", e.target.value)}
                  >
                    {COUNTRY_CODES.map((c) => (
                      <option key={c.name} value={c.dial}>
                        {c.dial === "other" ? "Other" : `${c.name} (${c.dial})`}
                      </option>
                    ))}
                  </select>
                  <input id="c-wa" type="tel" value={form.whatsappNumber}
                    onChange={(e) => set("whatsappNumber", e.target.value)}
                    placeholder={form.countryCode === "other" ? "Full international number" : "801 234 5678"} />
                </div>
                {errors.contact && <p className="field-error">{errors.contact}</p>}
              </div>
              <div className="field">
                <label htmlFor="c-subject">Subject</label>
                <input id="c-subject" type="text" value={form.subject} onChange={(e) => set("subject", e.target.value)} placeholder="e.g. Honeymoon in Zanzibar" />
              </div>
              <div className="field" data-invalid={errors.message ? "true" : undefined}>
                <label htmlFor="c-msg">Message</label>
                <textarea id="c-msg" rows={4} value={form.message} onChange={(e) => set("message", e.target.value)} placeholder="How can we help? Include your reference if you have one." />
                {errors.message && <p className="field-error">{errors.message}</p>}
              </div>
              <div className="field">
                <label>Preferred contact method</label>
                <div className="pill-group" role="group" aria-label="Preferred contact method">
                  {["Email", "WhatsApp"].map((m) => (
                    <button type="button" key={m}
                      className={`pill ${form.preferred === m ? "is-selected" : ""}`}
                      aria-pressed={form.preferred === m}
                      onClick={() => set("preferred", m)}>
                      {m}
                    </button>
                  ))}
                </div>
              </div>
              <div className={`consent-row ${errors.consent ? "is-invalid" : ""}`} data-invalid={errors.consent ? "true" : undefined}>
                <label className="consent-label">
                  <input type="checkbox" checked={form.consent} onChange={(e) => set("consent", e.target.checked)} />
                  <span>I'm happy for 513Travel to contact me about my enquiry.</span>
                </label>
                {errors.consent && <p className="field-error">{errors.consent}</p>}
              </div>
              {submitError && (
                <p className="form-error-top" role="alert">
                  {submitError} You can also email us at{" "}
                  <a className="inline-link" href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
                </p>
              )}
              <button type="submit" className="btn gold trip-submit" disabled={submitting}>
                {submitting ? "Sending…" : "Send message"}
              </button>
            </form>
          )}
        </div>
      </section>

      {/* ===== Trust note ===== */}
      <section className="section" style={{ paddingTop: 0 }}>
        <div className="container">
          <p className="demo-note demo-note--center">
            We usually reply within a short time by email or WhatsApp. For
            existing travel requests, please include your reference so we can
            help faster.
          </p>
        </div>
      </section>

      {/* ===== Mini FAQ ===== */}
      <section className="section">
        <div className="container">
          <div className="section-head">
            <div className="eyebrow">Good to know</div>
            <h2>Quick answers</h2>
          </div>
          <div className="grid grid-2">
            {FAQS.map((f) => (
              <div className="service-card" key={f.q}>
                <h3>{f.q}</h3>
                <p>{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== Final CTA ===== */}
      <section className="section section--green">
        <div className="container">
          <div className="feature-copy feature-solo">
            <div className="eyebrow">Ready when you are</div>
            <h2>Shall we start your journey?</h2>
            <p>Tell us what you need and we'll guide the next step — calmly, clearly and personally.</p>
            <div className="page-actions">
              <Link to="/plan-a-trip" className="btn light">Plan a Trip</Link>
              <a href={`mailto:${SUPPORT_EMAIL}`} className="btn outline-light">Email us</a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
